import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';
import { SurveyThemeProvider } from './context';
import { getSurveyDesign } from './SurveyTheme.graphql';
import formatEntity from './helpers';

/* A simple wrapper composes inner children with the fetched theming info. */
const SurveyTheme = ({ children, surveyId }) => {
  const { data, loading, error: fetchError, refetch } = useQuery(
    getSurveyDesign,
    {
      variables: {
        id: surveyId,
      },
      // acquire data even when an error occurs
      errorPolicy: 'all',
      partialRefetch: true,
    }
  );

  /* No-op on the loading state.
   * The wrapping component should know better how to compose with a representable skeleton.
   */
  if (loading) {
    return null;
  }

  const { theme, ...settings } = data?.survey?.design;

  const {
    selectedColorPalette,
    layout,
    surveyPage,
    surveyTitle,
    pageTitle,
    questionTitle,
    questionBody,
    button,
    error,
    exitLink,
    artifacts,
  } = theme;

  const { logo, progressBar } = settings;

  const surveyPageBackgroundImage = artifacts?.background?.url;

  const surveyTheme = {
    layout,
    surveyPage: {
      ...formatEntity(surveyPage),
      ...(surveyPageBackgroundImage && {
        backgroundImage: `url(${surveyPageBackgroundImage})`,
      }),
    },
    surveyTitle: formatEntity(surveyTitle),
    pageTitle: formatEntity(pageTitle),
    questionTitle: formatEntity(questionTitle),
    questionBody: {
      color: selectedColorPalette?.answerColor,
      ...formatEntity(questionBody),
    },
    button: formatEntity(button),
    error: formatEntity(error),
    exitLink: formatEntity(exitLink),
    ...(logo && {
      logo: formatEntity(logo),
    }),
    ...(progressBar && {
      progressBar: formatEntity(progressBar),
    }),
    ...formatEntity(selectedColorPalette),
  };

  return (
    <SurveyThemeProvider
      theme={{
        ...surveyTheme,
        ...(fetchError && { refetch, fetchError }),
      }}
    >
      {children}
    </SurveyThemeProvider>
  );
};

SurveyTheme.propTypes = {
  children: PropTypes.node.isRequired,
  surveyId: PropTypes.string.isRequired,
};

export default SurveyTheme;
